/**
 * Impact & Metrics Translations Module (English)
 * 
 * Contains: YFI Formula, YFI Rankings, Performance KPIs, Advanced Metrics, Engagement Metrics
 * 
 * @module impact
 * @language en
 */

export const impact = {
  impactMetrics: {
    pageTitle: "Impact & Metrics",
    pageSubtitle: "Measuring Youth Leadership, Mental Health & Economic Opportunity across 114 JCI countries"
  },
  fiiFormula: {
    stats: {
      mentalDisorders: "1 in 7 affected by mental disorders",
      mentalHealthLimiting: "68% cite mental health as limiting",
      emergingAccess: "Emerging markets (10-30% access)",
      digitalSkillsGap: "92% of jobs need digital skills (only 30% trained)",
      youthVsAdult: "13% global youth (vs 4.9% adults)",
      neetCrisis: "256M youth (20.4%) not in education/work",
      ssaAspiration: "52% of Sub-Saharan Africa youth want to start business",
      fundingGap: "Only 3% get funding ($1.7T gap globally)",
      womenGap: "Women entrepreneurs 35% underrepresented"
    },
    reasoning: {
      mentalHealth: "Heavy weighting reflects foundational importance - mental health enables all other capabilities",
      workforce: "Significant weighting reflects employment as the gateway to economic independence",
      entrepreneurship: "Significant weighting reflects innovation potential and self-employment necessity"
    },
    mentalHealthStat1: "1 in 7 affected by mental disorders",
    mentalHealthStat2: "68% cite mental health as limiting",
    mentalHealthStat3: "Emerging markets (10-30% access)",
    workforceStat1: "92% of jobs need digital skills (only 30% trained)",
    workforceStat2: "13% global youth unemployment (vs 4.9% adults)",
    workforceStat3: "256M youth (20.4%) not in education/work",
    entrepreneurshipStat1: "52% of Sub-Saharan Africa youth want to start business",
    entrepreneurshipStat2: "Only 3% get funding ($1.7T gap globally)",
    entrepreneurshipStat3: "Women entrepreneurs 35% underrepresented",
    mentalHealthReasoning: "Heavy weighting reflects foundational importance - mental health enables all other capabilities",
    workforceReasoning: "Significant weighting reflects employment as the gateway to economic independence",
    entrepreneurshipReasoning: "Significant weighting reflects innovation potential and self-employment necessity"
  },
  engagement: {
    title: "Engagement Metrics",
    subtitle: "Member participation and activity analysis",
    eventParticipation: "Event Participation",
    volunteerHours: "Volunteer Hours",
    projectContributions: "Project Contributions",
    monthlyActiveUsers: "Monthly Active Users"
  },
  network: {
    title: "Network Analysis",
    subtitle: "Global connections and collaboration patterns",
    totalConnections: "Total Connections",
    activeProjects: "Active Projects",
    strategicPartnerships: "Strategic Partnerships",
    knowledgeTransfer: "Knowledge Transfer",
    collectiveImpact: "Collective Impact",
    progress: "Progress",
    target: "Target"
  },
  trends: {
    title: "Trend Analysis & Predictions",
    subtitle: "Historical evolution of JCI and AI-based projections for 2026-2030",
    historical: "Historical Evolution (2015-2025)",
    predictions: "Projections 2026-2030",
    confidence: "confidence",
    keyTrends: "Key Trends Identified",
    sustainedGrowth: "Sustained Growth",
    digitalRevolution: "Digital Revolution",
    wellbeingFocus: "Wellbeing Focus",
    sustainability: "Sustainability",
    seasonalPatterns: "Seasonal Patterns",
    enrollments: "Enrollments",
    projects: "Projects",
    events: "Events",
    members: "Members",
    countries: "Countries",
    imh: "MHI"
  },
  status: {
    strong: "Strong",
    growing: "Growing",
    expanding: "Expanding",
    stable: "Stable",
    emerging: "Emerging",
    active: "Active",
    expansion: "Expansion",
    pilot: "Pilot",
    beta: "Beta"
  },
  advancedMetrics: {
    title: "Advanced Metrics",
    subtitle: "Detailed analysis of performance, conversion and key indicators for Vision 2040",
    conversionFunnel: "Conversion Funnel",
    websiteVisitors: "Website Visitors",
    interested: "Interested",
    candidates: "Candidates",
    interviewed: "Interviewed",
    activeMembers: "Active Members",
    totalConversionRate: "Total Conversion Rate",
    costPerAcquisition: "Cost per Acquisition",
    programROI: "Program ROI",
    performanceTrends: "Performance Trends",
    members: "Members",
    countries: "Countries",
    programs: "Programs",
    keyPerformanceIndicators: "Key Performance Indicators",
    retentionRate: "Retention Rate",
    membersActive12Months: "Members active for 12+ months",
    digitalEngagement: "Digital Engagement",
    onlineEventParticipation: "Online event participation",
    memberSatisfaction: "Member Satisfaction",
    avgProgramEvaluation: "Average program evaluation",
    averageLeadershipTime: "Average Leadership Time",
    joiningToFirstLeadership: "From joining to first leadership position",
    target: "Target",
    progress: "Progress"
  },
  performanceKPIs: {
    title: "Performance KPIs",
    subtitle: "Key performance indicators and targets for 2025",
    updatedAgo: "Updated",
    memberRetentionRate: "Member Retention Rate",
    annualRetentionVsTarget: "Annual member retention vs target",
    programROI: "Program ROI",
    trainingProgramsROI: "Return on investment for training programs",
    newMarkets: "New Markets",
    newCountriesEntered: "New countries entered this year",
    communityImpactScore: "Community Impact Score",
    aggregateCommunityImpact: "Aggregate community impact measurement",
    memberEngagement: "Member Engagement",
    activeParticipationJCI: "Active participation in JCI activities",
    leadershipPipeline: "Leadership Pipeline",
    membersInLeadershipTrack: "Members in leadership development track",
    performance: "performance",
    growth: "growth",
    impact: "impact",
    engagement: "engagement",
    target: "Target"
  },
  aiInsights: {
    title: "AI Insights Center",
    subtitle: "Predictive analytics, strategic recommendations, and 'what-if' scenarios for intelligent decisions",
    aiChat: "AI Chat",
    recommendations: "Recommendations",
    insights: "Insights",
    whatIf: "What-If",
    jciAssistant: "JCI Intelligent Assistant",
    askAboutData: "Ask questions about data, trends and strategies",
    askPlaceholder: "Ask about trends, analysis, or strategies...",
    greeting: "Hello! I'm the JCI insights assistant. How can I help you analyze data and discover strategic opportunities?",
    analyzing: "Analyzing your question about",
    growthPotential: "growth potential over the next 18 months",
    priority: "Priority",
    confidence: "Confidence",
    viewDetails: "View Details",
    expectedImpact: "Expected Impact",
    estimatedTime: "Estimated time",
    simulateScenario: "Simulate Scenario",
    strategicExpansion: "Strategic Expansion in Latin America",
    strategicExpansionDesc: "Data indicates 40% unexplored growth potential in countries like Colombia and Peru.",
    digitalMentalHealth: "Digital Mental Health Program",
    digitalMentalHealthDesc: "Implementing digital platform would increase engagement by 60% in young communities.",
    techPartnerships: "Technology Partnerships",
    techPartnershipsDesc: "Integration with local startups could accelerate innovation programs by 45%.",
    growth: "Growth",
    wellbeing: "Wellbeing",
    innovation: "Innovation",
    millennialEngagement: "Millennial Engagement",
    millennialEngagementDesc: "Accelerated growth in participation of 25-30 year olds",
    sustainableProjects: "Sustainable Projects",
    sustainableProjectsDesc: "ESG initiatives showed superior ROI",
    regionalRetention: "Regional Retention",
    regionalRetentionDesc: "Some regions need special attention",
    whatIfLeadership: "What if we increase investment in leadership programs by 25%?",
    whatIfLeadershipImpact: "35% increase in leadership quality, 2.3x more event participation",
    whatIfAI: "What if we implement AI in all administrative processes?",
    whatIfAIImpact: "40% reduction in administrative time, $2.1M annual savings",
    budget: "Budget",
    months: "months",
    focusYoungLeaders: "Focus on young leaders",
    totalAutomation: "Total automation",
    training: "Training",
    gradualMigration: "Gradual migration"
  },
  homeGlobalPresence: {
    title: "JCI Global Presence",
    subtitle: "Active in 114 countries across 4 regions",
    regions: "Regions",
    countries: "Countries",
    members: "Members",
    localOrgs: "Local Orgs"
  },
  fii: {
    // Core YFI Display
    title: "Youth Futures Index (YFI)",
    measuringYouthOpportunity: "Measuring Youth Opportunity Across 114 Countries",
    scale: "Scale: 0-100 (Higher = Better Youth Opportunity)",
    
    // Pillars
    mentalHealth: "Mental Health",
    workforceEducation: "Workforce & Education",
    entrepreneurshipSupport: "Entrepreneurship Support",
    
    // Pillar Quotes
    mentalHealthQuote: "Mental health is the foundation upon which all other capabilities are built.",
    workforceQuote: "Employment is the gateway to economic independence.",
    entrepreneurshipQuote: "Innovation and self-employment are essential pathways for youth opportunity.",
    
    // Labels
    globalCrisis: "Global Crisis",
    employmentBarrier: "Employment Barrier",
    mostVulnerable: "Most Vulnerable",
    skillGap: "Skills Gap",
    unemployment: "Youth Unemployment",
    neetCrisis: "NEET Crisis",
    aspiration: "Entrepreneurial Aspiration",
    fundingGap: "Funding Gap",
    genderGap: "Gender Gap",
    whyThisWeighting: "Why This Weighting?",
    ofTotalScore: "of total score",
    weight: "Weight",
    source: "Source",
    indicators: "Indicators",
    
    // Coverage Stats
    countriesMeasured: "Countries Measured",
    jciNationalOrgs: "JCI National Orgs",
    completeData: "Complete Data",
    countries: "countries",
    
    // Rankings Table
    averageFII: "Average YFI",
    highest: "Highest",
    lowest: "Lowest",
    pointRange: "Point Range",
    countriesRanked: "Countries Ranked",
    excellent: "Excellent",
    good: "Good",
    moderate: "Moderate",
    deficient: "Deficient",
    challenges: "Challenges",
    critical: "Critical",
    globalRankings: "Global YFI Index",
    searchCountry: "Search country...",
    allRegions: "All Regions",
    rank: "Rank",
    country: "Country",
    region: "Region",
    score: "Score",
    classification: "Classification",
    
    // Component Breakdown & Benchmarks (used in expanded table rows)
    componentBreakdown: "Component Breakdown",
    vsBenchmarks: "vs Benchmarks",
    vsGlobalAvg: "vs Global Avg",
    globalAverage: "Global Average",
    data: "Data",
    complete: "Complete",
    limited: "Limited",
    
    // Legend labels
    legendGood: "Good",
    legendModerate: "Moderate",
    legendChallenges: "Challenges",
    legendCritical: "Critical",
    
    // Call-to-Action
    useFIIData: "Use YFI Data",
    useFIIDataDesc: "Download index data and methodology documentation for research and advocacy",
    exportRankings: "Export Index (CSV)",
    downloadMethodology: "Download Methodology (PDF)",
    comingSoon: "Coming Soon",
    methodologyComingSoon: "Methodology PDF coming soon",
    researchCitations: "Research Citations",
    sourceWhoDescription: "Mental health service access, treatment gaps, policy coverage",
    sourceIloDescription: "Youth unemployment, NEET rates, workforce quality",
    sourceGemDescription: "Early-stage entrepreneurship, business environment",
    sourceWorldBankDescription: "Supporting data for entrepreneurship dimension",
    aboutMethodology: "About the Methodology",
    aboutMethodologyDesc: "The YFI uses verified data from WHO, ILO, World Bank, and other authoritative sources.",
    howToCite: "How to Cite",
    citationText: "JCI Development Councils Youth Futures Observatory (2025). Youth Futures Index.",
    
    // Insights Section
    insightsTitle: "Strategic Insights",
    insightsSubtitle: "Key findings from YFI analysis",
    finding: "Finding",
    dataEvidence: "Data Evidence",
    implication: "Implication",
    actionForJCI: "Action for JCI",
    insight1Title: "Mental Health as Foundation",
    insight2Title: "Skills-Employment Mismatch",
    insight3Title: "Regional Disparities",
    insight4Title: "Entrepreneurship Paradox",
    insight5Title: "Gender Gaps Persist",
    
    // Data Vintage & Methodology
    dataVintage: "Data Vintage",
    dataVintageDesc: "2023-2024 data from verified international sources",
    methodologyTitle: "YFI Methodology",
    methodologySubtitle: "How we calculate the Youth Futures Index",
    formulaExplanation: "Formula Explanation",
    pillarWeights: "Pillar Weights",
    dataSourcesTitle: "Data Sources",
    calculationFormula: "Calculation Formula",
    threeDimensions: "The Three Dimensions",
    officialDataSources: "Official Data Sources",
    dataCoverage: "Data Coverage",
    dataCoverageDesc: "The YFI covers 114 JCI member countries. Data availability varies by country and indicator.",
    
    // Methodology Principles
    openData: "Open Data",
    openDataDesc: "All source data is publicly available from authoritative institutions.",
    zeroSynthetic: "Zero Synthetic Data",
    zeroSyntheticDesc: "We use only real, verified data - never AI-generated or estimated values.",
    fullTransparency: "Full Transparency",
    fullTransparencyDesc: "Complete methodology and calculations are publicly documented.",
    scientificRigor: "Scientific Rigor",
    scientificRigorDesc: "Methodology reviewed and validated by research partners.",
    
    // Regional Analysis
    regionalPerformance: "Regional Performance",
    topPerformers: "Top Performers",
    bottomPerformers: "Bottom Performers",
    keyTakeaways: "Key Takeaways"
  },
  globalFIIMap: {
    title: "Global YFI Map",
    subtitle: "Explore youth opportunity metrics across all JCI countries",
    jciCountries: "JCI Countries",
    totalMembers: "Total Members",
    completeData: "Complete Data",
    globalRegions: "Global Regions",
    regionalOverview: "Regional Overview",
    countries: "Countries",
    avgFII: "Avg YFI",
    leader: "Leader",
    searchCountry: "Search country...",
    allRegions: "All Regions",
    allClassifications: "All Classifications",
    regionAsiaPacific: "Asia and the Pacific",
    regionEurope: "Europe",
    regionAmericas: "America",
    regionAfricaMe: "Africa and the Middle East",
    excellent: "Excellent",
    good: "Good",
    moderate: "Moderate",
    deficient: "Deficient",
    critical: "Critical",
    countriesTotal: "Countries Total",
    matching: "matching",
    fiiDataset: "YFI Dataset",
    withCompleteData: "with complete data",
    limitedData: "Limited Data",
    members: "members"
  },
  fiiInsights: {
    insight: "Insight",
    sourceAttribution: "JCI Impact Report 2025 | YFI Regional Analysis 2024",
    priority: {
      critical: "Critical",
      high: "High",
      medium: "Medium"
    },
    mentalHealth: {
      finding: "Mental health is the strongest predictor of overall YFI score. Countries with high mental health scores consistently rank higher.",
      mhScoreVariation: "MH Score Variation",
      wfEtScoreVariation: "WF & ET Score Variation",
      potentialImprovement: "Potential YFI Improvement",
      implication: "Investing in mental health yields the highest return on overall youth opportunity.",
      action: "Prioritize mental health programs in JCI initiatives, especially in low-scoring regions."
    },
    skillsMismatch: {
      finding: "Despite high education enrollment, youth unemployment remains elevated due to skills mismatch.",
      youthInEducation: "Youth in Education",
      youthUnemployment: "Youth Unemployment Rate",
      neetPopulation: "NEET Population",
      digitalSkillsJobs: "Jobs Needing Digital Skills",
      implication: "Traditional education is not preparing youth for the modern workforce.",
      action: "Develop skills-based training programs focused on digital and entrepreneurial competencies."
    },
    regionalDisparities: {
      finding: "Significant gaps exist between regions, with Africa and the Middle East facing the greatest challenges.",
      mentalHealthGap: "Mental Health Gap (Europe vs Africa-ME)",
      workforceGap: "Workforce Gap",
      entrepreneurshipGap: "Entrepreneurship Gap",
      implication: "A one-size-fits-all approach will not work; regional strategies are needed.",
      action: "Develop region-specific programs and increase resource allocation to underserved areas."
    },
    entrepreneurship: {
      finding: "High entrepreneurial aspiration exists in developing regions but access to capital is severely limited.",
      ssaRate: "SSA Entrepreneurship Rate",
      financingGap: "Global Financing Gap",
      africaMeShare: "Africa-ME Share of Global Startups",
      youthWanting: "Youth Wanting to Start Business",
      implication: "Untapped entrepreneurial potential exists, constrained by funding access.",
      action: "Create microfinancing programs and connect youth entrepreneurs with investors."
    },
    gender: {
      finding: "Women face compounded disadvantages across all three YFI dimensions.",
      depressionRates: "Women's Depression Rates",
      womenUnemployment: "Women's Unemployment",
      neetWomen: "Women Share of NEET",
      womenLedStartups: "Women-Led Startups",
      vcToWomen: "VC Funding to Women",
      implication: "Gender-specific barriers require targeted interventions.",
      action: "Launch women-focused leadership and entrepreneurship programs."
    }
  },
  fiiMethodology: {
    formulaExplanation: "The YFI is calculated as a weighted average of three dimensions: Mental Health (40%), Workforce & Education (30%), and Entrepreneurship Support (30%).",
    mentalHealth: {
      description: "Measures youth mental wellness, access to mental health services, and overall psychological wellbeing."
    },
    workforce: {
      description: "Assesses youth employment rates, education quality, skills development, and labor market access."
    },
    entrepreneurship: {
      description: "Evaluates business environment, access to financing, digital infrastructure, and startup ecosystem."
    },
    indicators: {
      mentalDisorderPrevalence: "Mental Disorder Prevalence",
      youthSuicideRate: "Youth Suicide Rate",
      mentalHealthServiceAccess: "Mental Health Service Access",
      mentalWellbeingIndex: "Mental Wellbeing Index",
      youthUnemploymentRate: "Youth Unemployment Rate",
      neetRate: "NEET Rate",
      tertiaryEducation: "Tertiary Education Enrollment",
      humanCapitalIndex: "Human Capital Index",
      easeOfDoingBusiness: "Ease of Doing Business",
      totalEntrepreneurialActivity: "Total Entrepreneurial Activity",
      accessToFinancing: "Access to Financing",
      digitalInfrastructure: "Digital Infrastructure"
    },
    dataTypes: {
      education: "Education",
      multiDimensional: "Multi-dimensional",
      wellbeing: "Wellbeing",
      digitalInfrastructure: "Digital Infrastructure"
    },
    access: {
      publicApiDownloads: "Public API & Downloads",
      ilostatPublicApi: "ILOSTAT Public API",
      publicDataPortal: "Public Data Portal",
      freeApiAccess: "Free API Access",
      publicReports: "Public Reports",
      publicData: "Public Data",
      annualPublicRelease: "Annual Public Release",
      publicStatistics: "Public Statistics"
    }
  }
} as const;
